'use client';

import { cn } from '@/lib/utils/cn';
import { cssVars } from '@/styles/theme';
import { AlertCircle, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import React from 'react';
import { Button } from './Button';

interface ErrorMessageProps {
  error: Error | string | null;
  title?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  variant?: 'default' | 'inline' | 'banner';
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({
  error,
  title,
  onRetry,
  onDismiss,
  className,
  variant = 'default',
}) => {
  const t = useTranslations('common.error');
  const tApiErrors = useTranslations('apiErrors');
  if (!error) return null;

  // Extract error message, ensuring we always have a valid string
  let errorMessage =
    typeof error === 'string'
      ? error.trim() || t('unknown')
      : error.message?.trim() || error.name || t('unknown');

  // Translate API error codes if they match the pattern
  if (
    errorMessage &&
    (errorMessage.startsWith('OFFER_') ||
      errorMessage.startsWith('REQUEST_') ||
      errorMessage.startsWith('CONNECTION_') ||
      errorMessage.startsWith('NETWORK_') ||
      errorMessage.startsWith('UNAUTHORIZED_') ||
      errorMessage.startsWith('RESOURCE_') ||
      errorMessage.startsWith('INVALID_') ||
      errorMessage.startsWith('SERVER_') ||
      errorMessage.startsWith('UNEXPECTED_') ||
      errorMessage.startsWith('CONTACT_') ||
      errorMessage.startsWith('FAQ_') ||
      errorMessage.startsWith('LOGIN_') ||
      errorMessage.startsWith('REGISTER_') ||
      errorMessage.startsWith('CREATE_REQUEST_') ||
      errorMessage.startsWith('UPDATE_REQUEST_'))
  ) {
    // Handle error codes with parameters (e.g., "OFFER_TECHNICAL_PROPOSAL_MIN_200:50")
    const [errorCode, param] = errorMessage.split(':');
    try {
      const translated = tApiErrors(errorCode as any);
      if (translated && translated !== errorCode) {
        errorMessage = translated.replace('{currentLength}', param || '0');
      }
    } catch {
      // If translation fails, use original message
    }
  }

  // Never show raw localization keys: use safe literal fallbacks so we never depend on t() for replacement
  const isRawKey = (s: string) =>
    typeof s === 'string' &&
    (s === 'common.error.defaultTitle' ||
      /^[a-zA-Z0-9_.]+\.[a-zA-Z0-9_.]+/.test(s) ||
      /^[a-zA-Z]+(\.[a-zA-Z0-9_]+)+$/.test(s));

  let errorTitle =
    title || (typeof error === 'string' ? t('defaultTitle') : error.name || t('defaultTitle'));
  if (typeof errorTitle === 'string' && isRawKey(errorTitle)) {
    errorTitle = 'Error';
  }
  // If message is a raw key, replace with generic message so we never show keys
  if (typeof errorMessage === 'string' && isRawKey(errorMessage)) {
    const fallbackMessage = t('unknown');
    errorMessage =
      typeof fallbackMessage === 'string' && !isRawKey(fallbackMessage)
        ? fallbackMessage
        : 'An error occurred';
  }

  if (variant === 'inline') {
    return (
      <div
        className={cn('flex items-start gap-2 rounded-lg border-2 p-3', className)}
        style={{ borderColor: cssVars.status.error }}
      >
        <AlertCircle
          className="mt-0.5 h-5 w-5 flex-shrink-0"
          style={{ color: cssVars.status.error }}
        />
        <div className="flex-1">
          <p className="text-sm font-medium sm:text-base" style={{ color: cssVars.status.error }}>
            {errorTitle}
          </p>
          <p className="mt-1 text-sm sm:text-base" style={{ color: cssVars.neutral.textSecondary }}>
            {errorMessage}
          </p>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex min-h-[44px] min-w-[44px] flex-shrink-0 touch-manipulation items-center justify-center rounded p-3 hover:bg-muted"
            aria-label="Dismiss error"
          >
            <X className="h-5 w-5" style={{ color: cssVars.neutral.textSecondary }} />
          </button>
        )}
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div
        className={cn('rounded-lg border-2 p-4', className)}
        style={{ borderColor: cssVars.status.error, backgroundColor: `${cssVars.status.error}10` }}
      >
        <div className="flex items-start gap-3">
          <AlertCircle
            className="mt-0.5 h-5 w-5 flex-shrink-0"
            style={{ color: cssVars.status.error }}
          />
          <div className="flex-1">
            <p className="text-sm font-medium sm:text-base" style={{ color: cssVars.status.error }}>
              {errorTitle}
            </p>
            <p className="mt-1 text-sm sm:text-base" style={{ color: cssVars.neutral.textSecondary }}>
              {errorMessage}
            </p>
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="flex min-h-[44px] min-w-[44px] flex-shrink-0 touch-manipulation items-center justify-center rounded p-3 hover:bg-muted"
              aria-label="Dismiss error"
            >
              <X className="h-5 w-5" style={{ color: cssVars.neutral.textSecondary }} />
            </button>
          )}
        </div>
        {onRetry && (
          <div className="mt-4">
            <Button onClick={onRetry} variant="outline" size="sm">
              {t('retry')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div
      className={cn('rounded-2xl border-2 p-8 text-center', className)}
      style={{ borderColor: cssVars.status.error }}
    >
      <AlertCircle className="mx-auto mb-4 h-12 w-12" style={{ color: cssVars.status.error }} />
      <h3 className="mb-2 text-lg font-semibold" style={{ color: cssVars.status.error }}>
        {errorTitle}
      </h3>
      <p className="mb-6 text-sm sm:text-base" style={{ color: cssVars.neutral.textSecondary }}>
        {errorMessage}
      </p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline">
          {t('retry')}
        </Button>
      )}
    </div>
  );
};

export default ErrorMessage;
